import React from 'react';
import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { BrowserRouter } from 'react-router-dom';
import {
  Alert,
  AppLogo,
  BreadcrumbTrail,
  Modal,
  Scrollable,
  SideNav,
  SideNavLink,
  Toolbar,
  UserAccount
} from './components';
import { moduleService } from './services';
import { securityService } from '../src/_global/services';
import {
  primary,
  secondary,
  error,
  warning,
  info,
  success
} from './styles/materialUiTheme';
import './styles/_bundle.scss';
import './styles/_button.scss';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { global } from './_config';
import { useStateReducer, useSubscription } from './hooks';
import { classNameBuilder } from './utilities';
import AppRoutes from './AppRoutes';
import AuthProvider from './AuthProvider';
import { StateTopicEnum } from './enums';
import { IBreadcrumb, IModuleConfig } from './interfaces';
import { Nullable } from './types';
import { faDatabase, faUpload } from '@fortawesome/free-solid-svg-icons';
import { UserPermissions } from '../src/definitions';
import { ItemPage, LibCard } from './_modules/cards/components';
import LibraryCard from './components/library/LibraryCard';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface IModuleConfigSummary {
  id: string;
  name: string;
  icon: IconProp;
  view_permission: string;
  openBlank?: boolean;
}

const theme = createTheme({
  palette: {
    primary,
    secondary,
    error,
    warning,
    info,
    success,
    mode: global.theme === 'dark' ? 'dark' : 'light'
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined'
      }
    }
  }
});

export default function App() {
  const { allowSettings } = global;
  const [state, setState] = useStateReducer({
    coverCount: 0,
    user: null,
    modules: []
  });
  const [modules, setModules] =
    useState<Nullable<IModuleConfigSummary[]>>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumb[]>([]);
  const [toolbarConfig, setToolbarConfig] = useState<IModuleConfig>();

  const loadModules = async () => {
    var modules = await moduleService.getAllModules();
    var moduleList = modules.map((el) => {
      return {
        id: el.id,
        name: el.name,
        icon: el.icon,
        view_permission: el.view_permission ?? ''
      };
    });

    let dataComparison: IModuleConfigSummary = {
      id: 'data-comparison',
      name: 'Data Comparison',
      icon: faDatabase,
      view_permission: UserPermissions.viewDataComparison,
      openBlank: true
    };

    let bulkCSV: IModuleConfigSummary = {
      id: 'bulk-csv',
      name: 'Bulk CSV Upload',
      icon: faUpload,
      view_permission: UserPermissions.viewBulkCSVUpload,
      openBlank: true
    };
    let specRoles: IModuleConfigSummary[] = [dataComparison, bulkCSV].filter(
      (mod) => securityService.checkRoleAccess(mod.view_permission)
    );
    let configRolesSummary: IModuleConfigSummary[] = moduleList?.filter((mod) =>
      securityService.checkRoleAccess(mod.view_permission)
    );
    setModules(configRolesSummary.concat(specRoles));
  };

  useSubscription(StateTopicEnum.Modal, (modal: boolean) => {
    setState({ coverCount: state.coverCount + (modal ? 1 : -1) });
  });

  useSubscription(StateTopicEnum.CurrentUserRoles, () => {
    loadModules();
  });

  useSubscription(StateTopicEnum.Alert, (alert: boolean) => {
    setState({ coverCount: state.coverCount + (alert ? 1 : -1) });
  });

  useSubscription(StateTopicEnum.User, (user: any) => {
    setState({ user });
  });

  const { user, coverCount } = state;
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <AuthProvider>
          <BrowserRouter>
            <Helmet>
              <title>Odynn Admin Portal</title>
            </Helmet>
            <div
              className={classNameBuilder(
                'h-app-container',
                coverCount > 0 ? 'has-cover' : '',
                user ? '' : 'signed-out'
              )}
            >
              {user && (
                <>
                  <BreadcrumbTrail breadcrumbs={breadcrumbs} />
                  <Toolbar config={toolbarConfig} />
                  {modules ? (
                    <SideNav>
                      <AppLogo />
                      <UserAccount allowSettings={allowSettings} />
                      <Scrollable>
                        {modules.map((m: any, i: number) => (
                          <SideNavLink
                            key={`${m.id}-${i}`}
                            to={`/${m.id}`}
                            icon={m.icon}
                            targetBlank={m.openBlank}
                          >
                            {m.name}
                          </SideNavLink>
                        ))}
                      </Scrollable>
                    </SideNav>
                  ) : (
                    <SideNav>
                      <AppLogo />
                      <UserAccount allowSettings={allowSettings} />
                      <Scrollable>
                        <h3 className='text-center'>No permissions</h3>
                      </Scrollable>
                    </SideNav>
                  )}
                </>
              )}

              <AppRoutes
                setBreadcrumbs={setBreadcrumbs}
                setToolbarConfig={setToolbarConfig}
              />
            </div>
            <Modal />
            <Alert />
          </BrowserRouter>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
